import React from 'react';
import { MenuItem, Select, SelectChangeEvent, Checkbox, SxProps, Box, Button } from '@mui/material';
import PipelineStatus from 'components/common/PipelineStatus';

type FilterStatusItem = {
  _id: string;
  label?: string;
  name?: string;
  color?: string;
};

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const getChipButtonSx = (isSelected: boolean) => ({
  backgroundColor: isSelected ? 'primary.main' : 'action.hover',
  color: isSelected ? 'common.white' : 'text.primary',
  border: 'none',
  px: 1.5,
  minHeight: '32px',
  '&:hover': {
    backgroundColor: isSelected ? 'primary.main' : 'action.selected',
  },
});

const getNextSelection = (selectedStatuses: string[], statusId: string, isAllSelected: boolean) => {
  if (isAllSelected) return [statusId];
  if (selectedStatuses.includes(statusId)) return selectedStatuses.filter(id => id !== statusId);
  return [...selectedStatuses, statusId];
};

const getStatusLabel = (status: FilterStatusItem) => status.label || status.name || status._id;

const StatusFilterSelector = ({
  statuses,
  selectedStatuses,
  onChangeStatus,
  layout = 'combobox',
  sx,
  testId,
}: {
  statuses: FilterStatusItem[];
  selectedStatuses: string[];
  onChangeStatus: (newSelectedStatuses: string[]) => void;
  layout?: 'combobox' | 'chip';
  sx?: SxProps;
  testId?: string;
}) => {
  if (layout === 'chip') {
    const isAllSelected = selectedStatuses.length === statuses.length;

    return (
      <Box
        sx={[{ display: 'flex', gap: 1, minWidth: 0, maxWidth: '100%' }, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}
        data-testid={testId}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, minWidth: 0, width: '100%' }}>
          <Button
            color="secondary"
            key="all"
            variant="outlined"
            sx={getChipButtonSx(isAllSelected)}
            onClick={() => onChangeStatus(statuses.map(status => status._id))}
          >
            All
          </Button>

          {statuses.map(status => (
            <Button
              color="secondary"
              key={status._id}
              sx={getChipButtonSx(selectedStatuses.includes(status._id) && !isAllSelected)}
              onClick={() => onChangeStatus(getNextSelection(selectedStatuses, status._id, isAllSelected))}
            >
              {getStatusLabel(status)}
            </Button>
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Select<string[]>
      value={selectedStatuses}
      onChange={(event: SelectChangeEvent<string[]>) => {
        const newSelectedStatuses =
          typeof event.target.value === 'string' ? event.target.value.split(',') : event.target.value;
        onChangeStatus(newSelectedStatuses);
      }}
      multiple
      renderValue={selected => {
        if (selected.length === 0) {
          return 'Select statuses';
        }
        return selected
          .map(id => {
            const status = statuses.find(s => s._id === id);
            return status ? getStatusLabel(status) : id;
          })
          .join(', ');
      }}
      MenuProps={MenuProps}
      sx={[{ width: { xs: '100%', md: '250px' } }, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}
      size="small"
    >
      {statuses.map(status => (
        <MenuItem key={status._id} value={status._id}>
          <Checkbox checked={selectedStatuses.includes(status._id)} />
          <PipelineStatus
            object={{
              statusLabel: getStatusLabel(status),
              color: status.color,
            }}
          />
        </MenuItem>
      ))}
    </Select>
  );
};

export default StatusFilterSelector;
